import { IAppAction, ActionType } from "./../Helpers";
import { IBulkWebsocketUpdate, BulkActionType } from "@vmosolutions/oc-interfaces";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { IRootState } from "../../reducers/CombinedReducers";
import { AnyAction } from "redux";
import { Type } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";

/**
 * Update Pairing Waterfall if temp pairings have just been created or pairings have been deleted.
 * @param payload -
 * @returns void
 */
export const updateWaterfall = (payload: IBulkWebsocketUpdate): ThunkAction<void, IRootState, object, AnyAction> => {
  return (dispatch: ThunkDispatch<IRootState, object, AnyAction>, getState: () => IRootState): void => {
    // If a pairing is being built, check the pairing waterfall to see if it has a matching
    const actionType = payload?.bulkOpsState?.actionType;
    // If there are waterfall pairings loaded into redux, update them as required
    const waterFallPairings = [...getState().crewSchedule.waterfallWindow.pairings];
    const successfullItems = payload?.bulkOpsState?.successfulEventIds ?? [];
    // If there are waterfall pairings to update continue.
    if (
      waterFallPairings?.length &&
      successfullItems?.length &&
      (actionType === BulkActionType.CREATE_PAIRING || actionType === BulkActionType.DELETE_PAIRING)
    ) {
      successfullItems?.forEach((item) => {
        const { tempId, objectId, shortId } = item ?? {};
        const itemIndex = waterFallPairings.findIndex((p) => p?._id === tempId);
        const currentPairing = waterFallPairings?.[itemIndex];
        if (currentPairing?._id.includes("temp-") && actionType === BulkActionType.CREATE_PAIRING) {
          // only update pairings if they currently have temp ids
          const updatedPairing = { ...currentPairing, _id: objectId, shortId: shortId };
          waterFallPairings[itemIndex] = updatedPairing; // UPDATE TEMP PAIRING
          // console.log("pairing created: ", updatedPairing);
        } else if (!currentPairing?._id.includes("temp-") && actionType === BulkActionType.DELETE_PAIRING) {
          waterFallPairings.splice(itemIndex, 1); // REMOVE DELETED PAIRING
          // console.log("pairing deleted: ", currentPairing?._id);
        }
      });
      // Dispatch the updated pairing waterfalls.
      dispatch({
        type: Type.UPDATE_WATERFALL_PAIRINGS,
        pairings: waterFallPairings,
      });
    }
  };
};

export const updateBulkBatch = (payload: IBulkWebsocketUpdate): IAppAction => {
  if (!!payload.batchId && !!payload.bulkOpsState.records) {
    //create a new batch
    return { type: ActionType.CREATE_BULK_BATCH, payload: payload };
  }
  //update a batch
  else {
    return { type: ActionType.UPDATE_BULK_BATCH, payload: payload };
  }
};

/**
 * A helper fnction to format the action type for the snack bar
 * @param actionType - enum BulkActionType
 * @returns a snack bar format string of the bulk event type
 */
export const formatBulkActionTypeSnackbarMessage = (actionType: BulkActionType, numOfRecords: number): string => {
  switch (actionType) {
    // CREATE
    case BulkActionType.CREATE_FLIGHT:
      return `Creating ${numOfRecords} Flights...`;
    case BulkActionType.CREATE_GROUND_EVENT:
      return `Creating ${numOfRecords} Ground Events...`;
    case BulkActionType.CREATE_PAIRING:
      return `Creating ${numOfRecords} Pairings...`;
    // UPDATE
    case BulkActionType.UPDATE_FLIGHT:
      return `Updating ${numOfRecords} Flights...`;
    case BulkActionType.UPDATE_GROUND_EVENT:
      return `Updating ${numOfRecords} Ground Events...`;
    // CANCEL
    case BulkActionType.CANCEL_FLIGHT:
      return `Canceling ${numOfRecords} Flights...`;
    case BulkActionType.CANCEL_GROUND_EVENT:
      return `Canceling ${numOfRecords} Ground Events...`;
    // DELETE
    case BulkActionType.DELETE_FLIGHT:
      return `Deleting ${numOfRecords} Flights...`;
    case BulkActionType.DELETE_GROUND_EVENT:
      return `Deleting ${numOfRecords} Ground Events...`;
    default:
      //shouldn't get to here
      return "Sending Request...";
  }
};

export const formatSuccessBulkSnackbarMessage = (
  actionType: BulkActionType,
  numOfRecords: number,
  success: number,
  fail: number
): string => {
  switch (actionType) {
    case BulkActionType.CREATE_FLIGHT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Flights Created Successfully`
        : `${success} Flights Created Successfully and ${fail} Failed`;

    case BulkActionType.CREATE_GROUND_EVENT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Ground Events Created Successfully`
        : `${success} Ground Events created Successfully and ${fail} Failed`;

    case BulkActionType.UPDATE_FLIGHT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Flights Updated Successfully`
        : `${success} Flights Updated Successfully and ${fail} Failed`;

    case BulkActionType.UPDATE_GROUND_EVENT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Ground Events Updated Successfully`
        : `${success} Ground Events Updated Successfully and ${fail} Failed`;

    case BulkActionType.CANCEL_FLIGHT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Flights Canceled Successfully`
        : `${success} Flights Canceled Successfully and ${fail} Failed`;

    case BulkActionType.CANCEL_GROUND_EVENT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Ground Events Canceled Successfully`
        : `${success} Ground Events Canceled Successfully and ${fail} Failed`;

    case BulkActionType.DELETE_FLIGHT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Flights Deleted Successfully`
        : `${success} Flights Deleted Successfully and ${fail} Failed`;

    case BulkActionType.DELETE_GROUND_EVENT:
      return success === numOfRecords
        ? `${success} of ${numOfRecords} Ground Events Deleted Successfully`
        : `${success} Ground Events Deleted Successfully and ${fail} Failed`;

    default:
      //shouldn't get to here
      console.warn("Unknown websocket type received.", actionType);
      return "Request Completed";
  }
};
